import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import socket, { sessionId, storage } from './socket';
import { store } from './store';
import {
  setPlayers, setIncomingChallenge, setCurrentRoom,
  setView, setChallengeDeclined, setPendingChallenge, setOpponentLeft,
  setOpponentDisconnected, setConnectionLost, setChallengeError,
  challengeCancelled, setRematchMine, setRematchOpponent, resetRematch,
  resetGame,
} from './store/slices/lobbySlice';
import { updateTTT, resetTTT } from './store/slices/ticTacToeSlice';
import {
  setPhase, setMyBoard, setOpponentBoard,
  updateBattle, setCurrentTurn, restoreBS, resetBS,
} from './store/slices/battleshipSlice';
import Login from './components/Login';
import Lobby from './components/Lobby';
import TicTacToe from './components/TicTacToe';
import Battleship from './components/Battleship';

export default function App() {
  const dispatch = useDispatch();
  const registered = useSelector((s) => s.user.registered);
  const view = useSelector((s) => s.lobby.view);
  const connectionLost = useSelector((s) => s.lobby.connectionLost);

  useEffect(() => {
    // ── (Re)register on every connection / reconnection ──
    socket.on('connect', () => {
      dispatch(setConnectionLost(false));
      const name = storage.getItem('gamePortal_name');
      if (name) {
        socket.emit('register', { name, sessionId });
      }
    });

    socket.on('disconnect', () => {
      dispatch(setConnectionLost(true));
    });

    // Fires on every failed connection attempt — covers the case where the
    // server is unreachable from the start (no 'disconnect' ever fires then)
    socket.on('connect_error', () => {
      dispatch(setConnectionLost(true));
    });

    // ── Registration ack: detect a game that no longer exists ──
    // If we think we're in a room but the server restored nothing (server
    // restarted, or the room timed out while we were gone), reset to lobby
    // instead of hanging on a dead game screen forever.
    socket.on('register:ack', ({ restored }) => {
      const lobby = store.getState().lobby;
      if (!restored && lobby.currentRoom) {
        dispatch(resetBS());
        dispatch(resetTTT());
        dispatch(resetGame());
        dispatch(setChallengeError('Your previous game has ended.'));
      }
    });

    // ── Lobby Events ──
    socket.on('lobby:update', (players) => dispatch(setPlayers(players)));

    socket.on('challenge:incoming', (data) => {
      const lobby = store.getState().lobby;
      // Busy (in a game, or already looking at another challenge) — politely
      // decline so the challenger isn't left waiting on an invisible modal.
      if (lobby.view !== 'lobby' || lobby.incomingChallenge) {
        socket.emit('challenge:decline', { challengerId: data.from });
        return;
      }
      dispatch(setIncomingChallenge(data));
    });
    socket.on('challenge:declined', () => dispatch(setChallengeDeclined(true)));
    socket.on('challenge:cancelled', ({ from }) => dispatch(challengeCancelled(from)));
    socket.on('challenge:unavailable', () =>
      dispatch(setChallengeError('That player is unavailable right now.')));
    socket.on('challenge:failed', () =>
      dispatch(setChallengeError('Could not start the game — the challenger is no longer available.')));

    socket.on('game:start', (room) => {
      dispatch(setPendingChallenge(null));
      // Covers both a fresh game and an agreed rematch restart
      dispatch(resetRematch());
      dispatch(setCurrentRoom(room));
      if (room.gameType === 'tictactoe') {
        // TTT is playable immediately — seed turn/marks from the room state
        // (battleship gets its state later via bs:gameReady)
        if (room.state) dispatch(updateTTT(room.state));
        dispatch(setView('tictactoe'));
      } else {
        dispatch(resetBS());
        dispatch(setView('battleship'));
      }
    });

    socket.on('game:opponentLeft', () => dispatch(setOpponentLeft(true)));
    socket.on('game:opponentDisconnected', () => dispatch(setOpponentDisconnected(true)));
    socket.on('game:opponentReconnected', () => dispatch(setOpponentDisconnected(false)));

    // ── Rematch ──
    socket.on('rematch:requested', () => dispatch(setRematchOpponent(true)));

    // ── Session Restoration (after refresh / reconnect / app restart) ──
    socket.on('session:restored', (data) => {
      if (!data || !data.room) return;
      dispatch(setCurrentRoom(data.room));
      const gt = data.room.gameType;
      dispatch(setView(gt === 'tictactoe' ? 'tictactoe' : 'battleship'));

      if (gt === 'battleship' && data.gameState) {
        dispatch(restoreBS(data.gameState));
      }
      if (gt === 'tictactoe' && data.gameState) {
        dispatch(updateTTT(data.gameState));
      }
      // Restore any in-flight rematch handshake (e.g. opponent asked for a
      // rematch while we were reconnecting)
      dispatch(setRematchMine(!!data.gameState?.rematchMine));
      dispatch(setRematchOpponent(!!data.gameState?.rematchOpponent));
    });

    // ── Tic Tac Toe Events ──
    socket.on('ttt:update', (data) => dispatch(updateTTT(data)));

    // ── Battleship Events ──
    socket.on('bs:waitingForOpponent', () => dispatch(setPhase('waiting')));

    socket.on('bs:gameReady', ({ currentTurn, myBoard }) => {
      dispatch(setMyBoard(myBoard));
      dispatch(setOpponentBoard(Array.from({ length: 10 }, () => Array(10).fill(0))));
      dispatch(setCurrentTurn(currentTurn));
      dispatch(setPhase('playing'));
    });

    socket.on('bs:update', (data) => dispatch(updateBattle(data)));

    // ── Reconnect promptly when the app returns to the foreground ──
    // On Android the WebView's timers and sockets freeze in the background;
    // without this nudge, reconnection can take a long time after resume.
    const onVisible = () => {
      if (document.visibilityState === 'visible' &&
          storage.getItem('gamePortal_name') && !socket.connected) {
        socket.connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    // ── Connect if returning from a previous session ──
    const savedName = storage.getItem('gamePortal_name');
    if (savedName && !socket.connected) {
      socket.connect();
    }

    return () => {
      socket.removeAllListeners();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dispatch]);

  if (!registered) return <Login />;

  // Blocking overlay while our own connection is down: the user always knows
  // they're offline, and can't fire moves that would queue up and replay stale.
  const connectionOverlay = connectionLost ? (
    <div className="modal-overlay">
      <div className="modal">
        <h3>📡 Connection lost</h3>
        <p>Trying to reconnect…</p>
        <div className="spinner" style={{ margin: '10px auto' }} />
      </div>
    </div>
  ) : null;

  let screen;
  switch (view) {
    case 'tictactoe': screen = <TicTacToe />; break;
    case 'battleship': screen = <Battleship />; break;
    default: screen = <Lobby />;
  }

  return (
    <>
      {connectionOverlay}
      {screen}
    </>
  );
}
